// ============================================================================
// PERSONAS WIDGET - Displays extracted personas in card layout
// Same visual style as RequirementsPanel persona cards
// ============================================================================

import React from 'react';
import { Users } from 'lucide-react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

const PersonasWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';

  const personas = state?.requirements?.personas ?? [];

  if (!state) return null;

  if (personas.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
          <Users size={20} className={isDark ? 'text-white/20' : 'text-slate-300'} />
        </div>
        <p className={`text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
          No personas extracted yet
        </p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-3">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {personas.map(persona => {
          const initials = persona.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
          return (
            <div
              key={persona.id}
              className={`rounded-xl overflow-hidden relative ${
                isDark
                  ? 'bg-white/[0.04] border border-white/10'
                  : 'bg-white border border-slate-200 shadow-sm'
              }`}
            >
              <div className={`absolute left-0 top-0 bottom-0 w-1 ${isDark ? 'bg-purple-400' : 'bg-purple-500'}`} />
              <div className="p-4 pl-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                    isDark ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-600'
                  }`}>
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <h3 className={`text-base font-bold truncate ${isDark ? 'text-white/90' : 'text-slate-900'}`}>
                      {persona.name}
                    </h3>
                    <p className={`text-sm truncate ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
                      {persona.role}
                    </p>
                  </div>
                </div>
                {persona.description && (
                  <p className={`text-sm mb-3 leading-relaxed line-clamp-2 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                    {persona.description}
                  </p>
                )}
                <div className="space-y-2">
                  {persona.goals.length > 0 && (
                    <div>
                      <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                        Goals
                      </p>
                      <ul className="space-y-0.5">
                        {persona.goals.slice(0, 3).map((goal) => (
                          <li key={goal} className={`text-sm leading-relaxed ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                            <span className={`inline-block w-1 h-1 rounded-full mr-2 align-middle ${isDark ? 'bg-purple-400' : 'bg-purple-500'}`} />
                            {goal}
                          </li>
                        ))}
                        {persona.goals.length > 3 && (
                          <li className={`text-xs ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                            +{persona.goals.length - 3} more
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                  {persona.painPoints.length > 0 && (
                    <div>
                      <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                        Pain Points
                      </p>
                      <ul className="space-y-0.5">
                        {persona.painPoints.slice(0, 3).map((pain) => (
                          <li key={pain} className={`text-sm leading-relaxed ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                            <span className={`inline-block w-1 h-1 rounded-full mr-2 align-middle bg-red-400`} />
                            {pain}
                          </li>
                        ))}
                        {persona.painPoints.length > 3 && (
                          <li className={`text-xs ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                            +{persona.painPoints.length - 3} more
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PersonasWidget;
